import React, { useState, useEffect, useCallback } from 'react';
import { periodicReviewApi, usersApi } from '../api';
import { toast } from 'sonner';
import { validateDateFields } from '../utils/dateValidation';
import { CalendarRange, Plus, Edit, Trash2, X, Loader2, CheckCircle, Clock, AlertCircle } from 'lucide-react';

const STATUS_MAP = {
  pendente:      { label: 'Pendente',    color: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30', icon: Clock },
  em_andamento:  { label: 'Em Andamento', color: 'bg-blue-500/20 text-blue-400 border border-blue-500/30', icon: AlertCircle },
  concluida:     { label: 'Concluída',   color: 'bg-green-500/20 text-green-400 border border-green-500/30', icon: CheckCircle },
};

const SYSTEM_VERSION = '1.0.0';

const EMPTY = {
  year: new Date().getFullYear(),
  review_date: '',
  next_review_date: '',
  status: 'pendente',
  responsible_id: '',
  system_version: SYSTEM_VERSION,
  total_incidents: 0,
  total_changes: 0,
  total_non_conformities: 0,
  performance_summary: '',
  security_assessment: '',
  improvement_actions: '',
  conclusions: '',
};

const PeriodicReview = () => {
  const [reviews, setReviews] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [detailView, setDetailView] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [rRes, uRes] = await Promise.all([periodicReviewApi.list(), usersApi.list()]);
      setReviews(rRes.data);
      setUsers(uRes.data?.items || uRes.data || []);
    } catch {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openModal = (review = null) => {
    if (review) {
      setEditing(review);
      setForm({
        year: review.year,
        review_date: review.review_date || '',
        next_review_date: review.next_review_date || '',
        status: review.status,
        responsible_id: review.responsible_id || '',
        system_version: review.system_version || SYSTEM_VERSION,
        total_incidents: review.total_incidents || 0,
        total_changes: review.total_changes || 0,
        total_non_conformities: review.total_non_conformities || 0,
        performance_summary: review.performance_summary || '',
        security_assessment: review.security_assessment || '',
        improvement_actions: review.improvement_actions || '',
        conclusions: review.conclusions || '',
      });
    } else {
      setEditing(null);
      setForm({ ...EMPTY, year: new Date().getFullYear() });
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.year) { toast.error('Informe o ano'); return; }
    const dateError = validateDateFields([
      { value: form.review_date, label: 'Data da Revisão' },
      { value: form.next_review_date, label: 'Próxima Revisão' },
    ]);
    if (dateError) { toast.error(dateError); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: parseInt(form.year),
        total_incidents: parseInt(form.total_incidents) || 0,
        total_changes: parseInt(form.total_changes) || 0,
        total_non_conformities: parseInt(form.total_non_conformities) || 0,
        responsible_id: form.responsible_id || null,
        review_date: form.review_date || null,
        next_review_date: form.next_review_date || null,
      };
      if (editing) {
        await periodicReviewApi.update(editing.id, payload);
        toast.success('Revisão atualizada');
      } else {
        await periodicReviewApi.create(payload);
        toast.success('Revisão criada');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (review) => {
    if (!window.confirm(`Confirmar aprovação da Revisão Periódica ${review.year}? Esta ação não pode ser desfeita.`)) return;
    try {
      await periodicReviewApi.update(review.id, { approve: true });
      toast.success('Revisão aprovada com sucesso');
      load();
    } catch {
      toast.error('Erro ao aprovar revisão');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta revisão periódica?')) return;
    try {
      await periodicReviewApi.delete(id);
      toast.success('Revisão excluída');
      load();
      if (detailView?.id === id) setDetailView(null);
    } catch {
      toast.error('Erro ao excluir');
    }
  };

  const sf = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarRange size={28} className="text-blue-400" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Revisão Periódica do Sistema</h1>
            <p className="text-gray-400 text-sm">F4-09 — ISO 9001:2015 — Revisão anual com avaliação de desempenho, segurança e melhorias</p>
          </div>
        </div>
        <button onClick={() => openModal()} className="btn-primary flex items-center gap-2">
          <Plus size={16} /> Nova Revisão
        </button>
      </div>

      {reviews.length === 0 ? (
        <div className="card text-center py-20 text-gray-400">
          <CalendarRange size={48} className="mx-auto mb-3 opacity-30" />
          <p>Nenhuma revisão periódica registrada</p>
          <button onClick={() => openModal()} className="btn-primary mt-4 mx-auto">Criar Primeira Revisão</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {reviews.map(rev => {
            const sm = STATUS_MAP[rev.status] || STATUS_MAP.pendente;
            const Icon = sm.icon;
            return (
              <div key={rev.id} className="card p-5 cursor-pointer hover:border-blue-500/50 transition-colors" onClick={() => setDetailView(rev)}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-bold text-white">Revisão {rev.year}</h3>
                    <p className="text-gray-400 text-sm">Sistema v{rev.system_version || SYSTEM_VERSION}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${sm.color}`}>
                    <Icon size={12} /> {sm.label}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center mb-3">
                  <div className="bg-gray-700/40 rounded p-2">
                    <p className="text-lg font-bold text-red-400">{rev.total_incidents}</p>
                    <p className="text-xs text-gray-500">Incidentes</p>
                  </div>
                  <div className="bg-gray-700/40 rounded p-2">
                    <p className="text-lg font-bold text-yellow-400">{rev.total_changes}</p>
                    <p className="text-xs text-gray-500">Mudanças</p>
                  </div>
                  <div className="bg-gray-700/40 rounded p-2">
                    <p className="text-lg font-bold text-orange-400">{rev.total_non_conformities}</p>
                    <p className="text-xs text-gray-500">NCs</p>
                  </div>
                </div>
                {rev.responsible_name && (
                  <p className="text-xs text-gray-400">Responsável: <span className="text-white">{rev.responsible_name}</span></p>
                )}
                {rev.approved_by_name && (
                  <p className="text-xs text-green-400 mt-1">✓ Aprovado por {rev.approved_by_name}</p>
                )}
                <div className="flex gap-2 mt-3" onClick={e => e.stopPropagation()}>
                  {rev.status !== 'concluida' && (
                    <button onClick={() => handleApprove(rev)} className="text-xs btn-secondary text-green-400 border-green-500/30 hover:bg-green-500/10 flex items-center gap-1">
                      <CheckCircle size={12} /> Aprovar
                    </button>
                  )}
                  <button onClick={() => openModal(rev)} className="text-xs btn-secondary flex items-center gap-1">
                    <Edit size={12} /> Editar
                  </button>
                  <button onClick={() => handleDelete(rev.id)} className="text-xs btn-secondary text-red-400 border-red-500/30 hover:bg-red-500/10 flex items-center gap-1">
                    <Trash2 size={12} /> Excluir
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Drawer */}
      {detailView && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setDetailView(null)}>
          <div className="w-full max-w-xl bg-gray-800 border-l border-gray-700 h-full overflow-y-auto p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Revisão {detailView.year}</h2>
              <button onClick={() => setDetailView(null)}><X size={20} className="text-gray-400" /></button>
            </div>
            {[
              { label: 'Resumo de Desempenho', value: detailView.performance_summary },
              { label: 'Avaliação de Segurança', value: detailView.security_assessment },
              { label: 'Ações de Melhoria', value: detailView.improvement_actions },
              { label: 'Conclusões', value: detailView.conclusions },
            ].map(({ label, value }) => value ? (
              <div key={label}>
                <p className="text-xs text-gray-400 font-medium mb-1">{label}</p>
                <p className="text-sm text-gray-300 bg-gray-700/40 rounded p-3 whitespace-pre-wrap">{value}</p>
              </div>
            ) : null)}
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h2 className="text-lg font-bold text-white">{editing ? 'Editar Revisão' : 'Nova Revisão Periódica'}</h2>
              <button onClick={() => setShowModal(false)}><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Ano *</label>
                  <input type="number" value={form.year} onChange={sf('year')} className="input" required />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Versão do Sistema</label>
                  <input value={form.system_version} onChange={sf('system_version')} className="input" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Data da Revisão</label>
                  <input type="date" value={form.review_date} onChange={sf('review_date')} className="input" min="1900-01-01" max="2099-12-31" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Próxima Revisão</label>
                  <input type="date" value={form.next_review_date} onChange={sf('next_review_date')} className="input" min="1900-01-01" max="2099-12-31" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Responsável</label>
                  <select value={form.responsible_id} onChange={sf('responsible_id')} className="input">
                    <option value="">— Selecionar —</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Status</label>
                  <select value={form.status} onChange={sf('status')} className="input">
                    <option value="pendente">Pendente</option>
                    <option value="em_andamento">Em Andamento</option>
                    <option value="concluida">Concluída</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Total de Incidentes</label>
                  <input type="number" min="0" value={form.total_incidents} onChange={sf('total_incidents')} className="input" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Total de Mudanças</label>
                  <input type="number" min="0" value={form.total_changes} onChange={sf('total_changes')} className="input" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Total de NCs</label>
                  <input type="number" min="0" value={form.total_non_conformities} onChange={sf('total_non_conformities')} className="input" />
                </div>
              </div>
              {[
                { key: 'performance_summary', label: 'Resumo de Desempenho do Sistema' },
                { key: 'security_assessment', label: 'Avaliação de Segurança (F4-05)' },
                { key: 'improvement_actions', label: 'Ações de Melhoria Identificadas' },
                { key: 'conclusions', label: 'Conclusões Gerais' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-sm text-gray-300 mb-1">{label}</label>
                  <textarea rows={3} value={form[key]} onChange={sf(key)} className="input resize-none" />
                </div>
              ))}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Cancelar</button>
                <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                  {editing ? 'Salvar Alterações' : 'Criar Revisão'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodicReview;
